// src/components/player/tiles/Tile3ZScoreSkill.js
// Tile 3: Skill/Predictive Z-Score Analysis
// Displays player's skill metrics (BB%, K%, ISO, BABIP, OPS) with Z-scores
// NOTE: K% is INVERTED - lower K% = positive Z-score = good

import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { Target } from 'lucide-react';

const Tile3ZScoreSkill = ({ data, loading, error }) => {
  // Tile wrapper component
  const Tile = ({ children }) => (
    <div className={`${dynastyTheme.components.card.base} h-full`}>
      {children}
    </div>
  );

  // Format stat name for display
  const formatStatName = (fieldName) => {
    const labels = {
      'bb_pct': 'BB%',
      'k_pct': 'K%',
      'iso': 'ISO',
      'babip': 'BABIP',
      'ops': 'OPS'
    };
    return labels[fieldName] || fieldName.toUpperCase();
  };

  // Format raw stat value
  const formatRawValue = (fieldName, value) => {
    if (value === null || value === undefined) return '-';
    
    // Percentages
    if (['bb_pct', 'k_pct'].includes(fieldName)) {
      return `${value.toFixed(1)}%`;
    }
    
    // Rate stats (3 decimals)
    return value.toFixed(3);
  };

  // Get Z-score indicator emoji
  const getZScoreIndicator = (zScore) => {
    if (zScore >= 2.0) return '🚀';
    if (zScore >= 1.0) return '⭐';
    if (zScore >= 0.5) return '↑';
    if (zScore >= -0.5) return '→';
    return '↓';
  };

  // Get Z-score color class
  const getZScoreColor = (zScore) => {
    if (zScore >= 2.0) return dynastyTheme.classes.text.success;
    if (zScore >= 1.0) return dynastyTheme.classes.text.primary;
    if (zScore >= -0.5) return dynastyTheme.classes.text.white;
    return dynastyTheme.classes.text.error;
  };

  // Check if stat is K% (inverted)
  const isInverted = (fieldName) => fieldName === 'k_pct';

  // Check data structure — V12: canonical cache record under data.mlb
  const mlb = data?.mlb;
  const rawStats = mlb?.stats_snapshot?.raw_stats;
  const hasPlayerData = !!mlb;
  const hasRawStats = !!rawStats;
  const hasZScores = !!mlb?.category_z_scores;
  const hasPercentiles = !!mlb?.percentiles;

  return (
    <Tile>
      <div className="pt-1 px-4 pb-3 flex flex-col tile-scroll" style={{ height: '182px', overflow: 'auto' }}>
        <h4 className={`text-base font-semibold ${dynastyTheme.classes.text.primary} mb-3 flex items-center`}>
          <Target className="w-4 h-4 mr-2" />
          🎯 Skill Metrics
        </h4>
        
        {loading ? (
          <div className={`flex-1 flex items-center justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              Loading analytics...
            </div>
          </div>
        ) : error ? (
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.error} mb-2`}>
              {error}
            </div>
          </div>
        ) : hasPlayerData && hasRawStats && hasZScores ? (
          <div className="flex-1 flex flex-col">
            {/* Skill Z-Score Grid */}
            <div className="flex-1 overflow-y-auto space-y-1">
              {Object.keys(rawStats).map((stat) => {
                const rawValue = rawStats[stat];
                const zScore = mlb.category_z_scores[stat];
                const percentile = hasPercentiles ? mlb.percentiles[stat] : null;
                const inverted = isInverted(stat);
                
                return (
                  <div key={stat} className="grid grid-cols-4 gap-2 text-sm items-center py-1">
                    {/* Stat Name & Value */}
                    <div className={`col-span-1 ${dynastyTheme.classes.text.neutralLight}`}>
                      <div>
                        {formatStatName(stat)}
                        {inverted && <span className="text-sm ml-1" title="K% is inverted: lower is better">*</span>}
                      </div>
                      <div className="text-xs opacity-75">{formatRawValue(stat, rawValue)}</div>
                    </div>

                    {/* Z-Score */}
                    <div className={`col-span-2 text-center`}>
                      <span className={`text-sm ${getZScoreColor(zScore)}`}>
                        {zScore >= 0 ? '+' : ''}{zScore.toFixed(2)} {getZScoreIndicator(zScore)}
                      </span>
                    </div>

                    {/* Percentile */}
                    <div className={`col-span-1 text-right ${dynastyTheme.classes.text.neutral}`}>
                      {percentile ? `${percentile}th` : '-'}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Footer with MLB population info and K% note */}
            <div className={`text-xs ${dynastyTheme.classes.text.neutral} pt-2 mt-2 border-t border-gray-700 space-y-1`}>
              {mlb?.total_players && (
                <div>MLB Population: {mlb.total_players} hitters</div>
              )}
              <div className="text-xs opacity-75">*K% inverted: lower is better</div>
            </div>
          </div>
        ) : (
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} text-center`}>
              No skill data available
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
};

export default Tile3ZScoreSkill;
